import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { 
  PhotoIcon, 
  DocumentTextIcon,
  XMarkIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

const CreateProject = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    construction_types: '',
    estimated_amount: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_password: '',
    contractor_id: '',
    is_urgent: false
  });

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [customType, setCustomType] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // 파일 업로드 상태
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  // 기존 고객 정보
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [phoneCheckTimer, setPhoneCheckTimer] = useState(null);
  // 고객명 자동입력(전화 끝4자리) 추적용 — 직접 입력한 이름은 덮어쓰지 않음
  const autoNameRef = useRef('');

  // 설정에서 공사 유형 불러오기
  const { data: settings } = useQuery(
    'admin-settings',
    () => api.get('/admin/settings').then(res => res.data),
    {
      refetchOnMount: 'always',
      staleTime: 0,
      cacheTime: 0
    }
  );

  // 소장 목록 불러오기
  const { data: contractorsData } = useQuery(
    'admin-contractors',
    () => api.get('/admin/contractors').then(res => res.data)
  );
  
  const contractors = contractorsData?.contractors || [];

  // 휴가 중인 사용자 목록 불러오기
  const { data: vacationUsers } = useQuery(
    'vacation-users',
    () => api.get('/users/vacation/list').then(res => res.data)
  );

  const constructionTypes = settings?.construction_types ? 
    settings.construction_types.split(',').map(type => type.trim()) : [];
  
  const activeContractors = contractors?.filter(contractor => contractor.is_active) || [];
  
  // 휴가 중인 사용자 ID 목록 생성
  const vacationUserIds = vacationUsers?.map(user => user.id) || [];

  const createProjectMutation = useMutation(
    (data) => {
      const formData = new FormData();
      
      // 프로젝트 데이터 추가
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });
      
      // 파일 추가
      console.log('Adding files to FormData:', selectedFiles.length);
      selectedFiles.forEach((file, index) => {
        console.log(`Adding file ${index + 1}:`, file.name, file.type, file.size);
        formData.append('files', file);
      });
      
      return api.post('/admin/projects', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    {
      onSuccess: (response) => {
        console.log('Project created successfully:', response.data);
        toast.success('프로젝트가 성공적으로 등록되었습니다.');
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries('admin-customers'); // 고객 목록도 새로고침
        navigate('/admin/projects');
      },
      onError: (error) => {
        console.error('Create project error:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        
        const errorData = error.response?.data;
        const errorMessage = errorData?.error || '프로젝트 등록에 실패했습니다.';
        const errorDetails = errorData?.details || errorData?.message;
        const errorField = errorData?.field;
        
        // 중복 오류인 경우 더 명확한 메시지 표시
        if (errorMessage === 'Duplicate entry' && errorDetails) {
          toast.error(errorDetails);
          
          // 해당 필드로 포커스 이동
          if (errorField === 'email') {
            document.querySelector('input[name="customer_email"]')?.focus();
          } else if (errorField === 'phone') {
            document.querySelector('input[name="customer_phone"]')?.focus();
          }
        } else if (errorDetails) {
          toast.error(`${errorMessage}: ${errorDetails}`);
        } else {
          toast.error(errorMessage);
        }
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 선택된 공사 유형 또는 커스텀 유형 (단일 선택)
    const allTypes = customType.trim() ? [customType.trim()] : selectedTypes;
    
    // 숫자 필드 처리
    const submitData = {
      ...formData,
      construction_types: allTypes.join(','),
      estimated_amount: formData.estimated_amount ? parseInt(formData.estimated_amount) : null
    };
    
    console.log('Submitting data:', submitData);
    console.log('Form data before submit:', formData);
    console.log('All types:', allTypes);
    
    createProjectMutation.mutate(submitData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // 전화번호 입력 시 자동으로 뒷자리 4개를 비밀번호로 설정
    if (field === 'customer_phone') {
      const phoneDigits = value.replace(/[^0-9]/g, '');
      if (phoneDigits.length >= 4) {
        const autoPassword = phoneDigits.slice(-4);
        setFormData(prev => {
          // 비어있거나 직전 자동입력값 그대로면 → 항상 최신 끝 4자리로 갱신 (직접 입력한 이름이면 유지)
          const isAutoOrEmpty = !prev.customer_name || prev.customer_name === autoNameRef.current;
          if (isAutoOrEmpty) autoNameRef.current = autoPassword;
          return {
            ...prev,
            [field]: value,
            customer_password: autoPassword,
            customer_name: isAutoOrEmpty ? autoPassword : prev.customer_name
          };
        });
      }
      
      // 전화번호로 기존 고객 확인 (디바운스)
      if (phoneCheckTimer) {
        clearTimeout(phoneCheckTimer);
      }
      
      if (value.length >= 10) {
        const timer = setTimeout(async () => {
          try {
            const response = await api.get(`/admin/customers/check?phone=${value}`);
            if (response.data.exists) {
              setExistingCustomer(response.data.customer);
              // 기존 고객 정보로 폼 채우기
              setFormData(prev => ({
                ...prev,
                customer_name: response.data.customer.name || prev.customer_name,
                customer_email: response.data.customer.email || prev.customer_email
              }));
            } else {
              setExistingCustomer(null);
            }
          } catch (error) {
            console.error('Error checking customer:', error);
          }
        }, 500); // 0.5초 후 확인
        
        setPhoneCheckTimer(timer);
      } else {
        setExistingCustomer(null);
      }
    }
  };

  const handleTypeToggle = (type) => {
    // 단일 선택으로 변경 - 이미 선택된 것을 클릭하면 해제, 아니면 교체
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? []
        : [type]
    );
  };

  const handleCustomTypeAdd = () => {
    if (customType.trim()) {
      // 단일 선택으로 변경 - 직접 입력 시 기존 선택 대체
      setSelectedTypes([customType.trim()]);
      setCustomType('');
      setShowCustomInput(false);
    }
  };

  const removeSelectedType = (type) => {
    setSelectedTypes(prev => prev.filter(t => t !== type));
  };
  
  // 파일 선택 처리
  const handleFileSelect = (e) => {
    try {
      const files = Array.from(e.target.files);
      console.log('Selected files:', files.map(f => ({ name: f.name, type: f.type, size: f.size })));
      
      if (files.length === 0) {
        console.log('No files selected');
        return;
      }
      
      const totalSize = [...selectedFiles, ...files].reduce((sum, file) => sum + file.size, 0);
      
      if (totalSize > 50 * 1024 * 1024) { // 50MB 제한
        toast.error('전체 파일 크기는 50MB를 초과할 수 없습니다.');
        return;
      }
      
      // 파일 형식 검증 (선택사항)
      const validFiles = files.filter(file => {
        const ext = file.name.split('.').pop().toLowerCase();
        const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 
                                'xls', 'xlsx', 'ppt', 'pptx', 'hwp', 'hwpx', 'dwg', 'dxf', 
                                'zip', 'rar', '7z'];
        
        if (!validExtensions.includes(ext)) {
          console.warn(`File ${file.name} with extension ${ext} might not be supported`);
        }
        return true; // 모든 파일 허용
      });
      
      setSelectedFiles(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length}개 파일이 선택되었습니다.`);
      e.target.value = null; // 같은 파일 재선택 가능하도록
    } catch (error) {
      console.error('File selection error:', error);
      toast.error('파일 선택 중 오류가 발생했습니다.');
    }
  };
  
  // 파일 제거
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // 파일 크기 포맷
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // 파일 아이콘 가져오기
  const getFileIcon = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <PhotoIcon className="h-5 w-5 text-blue-500" />;
    }
    return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">새 프로젝트 등록</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">새로운 건축/시공 프로젝트를 등록합니다.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">프로젝트 정보</h2>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                프로젝트 제목 *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="예: 강남구 아파트 방수공사"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                공사 유형 *
              </label>
              
              {/* 설정된 공사 유형이 있을 때만 표시 */}
              {constructionTypes.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">등록된 공사 유형 (1개 선택):</p>
                  <div className="flex flex-wrap gap-2">
                    {constructionTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleTypeToggle(type)}
                        className={`px-3 py-1.5 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm transition-colors ${
                          selectedTypes.includes(type)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 선택된 공사 유형 표시 */}
              {selectedTypes.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">선택된 공사 유형:</p>
                  <div className="flex flex-wrap gap-2">
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                    >
                      {selectedTypes[0]}
                      <button
                        type="button"
                        onClick={() => setSelectedTypes([])}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        ×
                      </button>
                    </span>
                  </div>
                </div>
              )}

              {/* 커스텀 입력 */}
              <div className="space-y-2">
                {!showCustomInput ? (
                  <button
                    type="button"
                    onClick={() => setShowCustomInput(true)}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    + 직접 입력하기
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customType}
                      onChange={(e) => setCustomType(e.target.value)}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="공사 유형을 입력하세요"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCustomTypeAdd();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleCustomTypeAdd}
                      className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      추가
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCustomInput(false);
                        setCustomType('');
                      }}
                      className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      취소
                    </button>
                  </div>
                )}
              </div>
              
              {/* 공사 유형 선택 안내: 관리자 숙지 내용으로 제거됨 */}
              
              {/* 최종 검증을 위한 hidden input */}
              <input
                type="hidden"
                value={[...selectedTypes, customType].filter(Boolean).join(',')}
                required={selectedTypes.length === 0 && !customType.trim()}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                예상 금액 (원)
              </label>
              <input
                type="number"
                value={formData.estimated_amount}
                onChange={(e) => handleChange('estimated_amount', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="5000000"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                주소
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="서울특별시 강남구 논현동 123-45"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                프로젝트 설명 *
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="프로젝트의 상세 내용을 입력하세요..."
              />
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_urgent"
                  checked={formData.is_urgent}
                  onChange={(e) => handleChange('is_urgent', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="is_urgent" className="ml-2 block text-sm text-gray-900">
                  긴급 프로젝트
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">고객 정보</h2>
          
          {/* 기존 고객 알림 */}
          {existingCustomer && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">기존 고객입니다</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>총 {existingCustomer.project_count}개 프로젝트 진행</p>
                    {existingCustomer.total_amount > 0 && (
                      <p>누적 금액: {existingCustomer.total_amount.toLocaleString()}원</p>
                    )}
                    {existingCustomer.is_vip && (
                      <p className="font-semibold text-blue-900 mt-1">⭐ VIP 고객</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                고객명 *
              </label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => handleChange('customer_name', e.target.value)}
                required
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="홍길동"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                연락처 *
              </label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => handleChange('customer_phone', e.target.value)}
                required
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="010-1234-5678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                이메일
              </label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleChange('customer_email', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="customer@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                접속 비밀번호 *
              </label>
              <input
                type="text"
                value={formData.customer_password}
                onChange={(e) => handleChange('customer_password', e.target.value)}
                required
                maxLength="6"
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono"
                placeholder="전화번호 입력 시 자동 생성"
              />
              <p className="mt-1 text-xs text-gray-500">
                고객이 프로젝트에 접속할 때 사용하는 비밀번호입니다. (전화번호 뒷자리 4개 자동 생성)
              </p>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                담당 소장 선택
              </label>
              <select
                value={formData.contractor_id}
                onChange={(e) => handleChange('contractor_id', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">나중에 배정하기</option>
                {activeContractors.map((contractor) => {
                  const isOnVacation = vacationUserIds.includes(contractor.id);
                  const vacationUser = vacationUsers?.find(user => user.id === contractor.id);
                  
                  return (
                    <option 
                      key={contractor.id} 
                      value={contractor.id}
                      className={isOnVacation ? 'text-gray-400 line-through' : ''}
                    >
                      {contractor.name} ({contractor.company_name})
                      {isOnVacation && ` [휴가 중: ${vacationUser?.days_remaining}일 남음]`}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                지금 선택하지 않으면 나중에 프로젝트 관리에서 배정할 수 있습니다.
              </p>
              {formData.contractor_id && vacationUserIds.includes(parseInt(formData.contractor_id)) && (
                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        선택하신 소장님은 현재 휴가 중입니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">첨부파일</h2>
          
          <div className="space-y-4">
            {/* 파일 업로드 영역 */}
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <PaperClipIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                클릭하여 파일을 선택하거나 여기에 파일을 드래그하세요
              </p>
              <p className="text-xs text-gray-500 mt-1">
                지원 형식: 이미지, PDF, DOC, XLS, PPT, HWP, DWG, DXF, ZIP 등 (최대 50MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.hwpx,.zip,.rar,.7z,.dwg,.dxf,application/acad,application/x-acad,application/autocad-dxf,drawing/x-dwf,application/dxf"
              />
            </div>
            
            {/* 선택된 파일 목록 */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  선택된 파일 ({selectedFiles.length}개)
                </p>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getFileIcon(file)}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <p className="text-xs text-gray-500">
              * 프로젝트 등록 시 첨부한 파일은 프로젝트 정보에 별도로 표시됩니다.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            type="button"
            onClick={() => navigate('/admin/projects')}
            className="w-full sm:w-auto bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-3 sm:py-2 rounded-md text-sm font-medium order-2 sm:order-1"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={createProjectMutation.isLoading}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-4 py-3 sm:py-2 rounded-md text-sm font-medium order-1 sm:order-2"
          >
            {createProjectMutation.isLoading ? '등록 중...' : '프로젝트 등록'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProject;